import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircleIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';

type HelpTooltipProps = {
  text: string;
  className?: string;
};

// Keep the panel inside the viewport with a small gutter on every edge.
const EDGE_GAP = 8;
// Gap between the trigger icon and the panel.
const TRIGGER_GAP = 8;

// Lightweight help icon + tooltip. Opens on hover/focus (desktop) and on tap
// (mobile), and is dismissible via outside click or Escape. The panel is
// rendered with position: fixed and clamped to the viewport so it can never be
// clipped by the screen edges, even on narrow mobile screens or when the icon
// sits near the right edge.
export const HelpTooltip = ({ text, className }: HelpTooltipProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  const reposition = useCallback(() => {
    const trigger = wrapRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    // Center horizontally on the trigger, then clamp within the viewport.
    const desiredLeft = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;
    const maxLeft = vw - panelRect.width - EDGE_GAP;
    const left = Math.max(EDGE_GAP, Math.min(desiredLeft, maxLeft));

    // Prefer above the trigger; flip below if there isn't enough room.
    const above = triggerRect.top - TRIGGER_GAP - panelRect.height;
    const below = triggerRect.bottom + TRIGGER_GAP;
    const top = above >= EDGE_GAP ? above : Math.min(below, vh - panelRect.height - EDGE_GAP);

    setPos({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={cn('relative inline-flex align-middle', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={t('help')}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-faint outline-none transition-colors hover:text-ink-muted focus-visible:ring-2 focus-visible:ring-brand-200"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <HelpCircleIcon className="h-4 w-4" />
      </button>
      {open && (
        <span
          ref={panelRef}
          id={tooltipId}
          role="tooltip"
          style={{ left: pos.left, top: pos.top }}
          className="fixed z-50 max-w-[min(16rem,calc(100vw-1rem))] whitespace-normal break-words rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-normal leading-snug text-ink-soft shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
};
